import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
} from '@mui/material'
import { ChangeEvent, useState, useEffect, useCallback } from 'react'
import ToastMessage from '../ToastMessage'
import { toastOptions } from '../../models'
import {
  BUTTON_TEXT,
  emptyErrorMsg,
  errorReadMsg,
  erroruploadfailed,
  errorvalidatefirst,
  invalidMsg,
  invalidMsgTwo,
  selectCSV,
  UploadCSV,
  uploadSuccess,
  validMsg,
} from '../../constants'
import { bulkUploadService } from '../../../../services/AdminService'

const AdminUploadDialog = ({ open, onClose, item }: any) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [csvData, setCsvData] = useState<any[]>([])
  const [validated, setValidated] = useState(false)
  const [uploading, setUploading] = useState(false)
  const resetUploadState = useCallback(() => {
    setSelectedFile(null)
    setCsvData([])
    setValidated(false)
    setUploading(false)
  }, [])
  useEffect(() => {
    if (!open) {
      resetUploadState()
    }
  }, [open, resetUploadState])

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setSelectedFile(file)
  }
  const handleValidate = useCallback(async () => {
    if (!selectedFile) {
      toast(<ToastMessage message={selectCSV} />, {
        ...toastOptions,
        type: 'error',
      })
      return
    }

    try {
      const data = await readFile(selectedFile)

      if (!data?.length) {
        toast(<ToastMessage message={emptyErrorMsg} />, {
          ...toastOptions,
          type: 'error',
        })
        onClose()
        return
      }

      const expectedHeaders = Object?.keys(data?.[0]).filter(
        (h) => h && h?.trim() !== ''
      )

      const invalidRows: any[] = []
      const validRows: any[] = []

      data?.forEach((row, index) => {
        const keys = Object.keys(row)
        const invalidHeaderData = keys.some(
          (k) =>
            !expectedHeaders?.includes(k) &&
            k?.trim() !== '' &&
            row?.[k] &&
            String(row?.[k])?.trim() !== ''
        )
        const hasEmptyField = expectedHeaders?.some((h) => {
          if (['packages', 'panel'].includes(item?.toLowerCase())) {
            return false
          }
          const val = row?.[h]
          return val === null || val === undefined || String(val).trim() === ''
        })

        if (invalidHeaderData || hasEmptyField) {
          invalidRows?.push({ rowNumber: index + 1, data: row })
        } else {
          validRows?.push(row)
        }
      })

      if (invalidRows.length > 0) {
        toast(
          <ToastMessage
            message={`${invalidMsg} ${invalidRows.length}${invalidMsgTwo}`}
          />,
          { ...toastOptions, type: 'error' }
        )
        onClose()
        return
      }
      setCsvData(validRows)
      setValidated(true)
      toast(<ToastMessage message={validMsg} />, {
        ...toastOptions,
        type: 'success',
      })
    } catch (err) {
      toast(<ToastMessage message={errorReadMsg} />, {
        ...toastOptions,
        type: 'error',
      })
      onClose()
    }
  }, [selectedFile, item, onClose])

  const handleProcess = async () => {
    if (!csvData.length) {
      toast(<ToastMessage message={errorvalidatefirst} />, {
        ...toastOptions,
        type: 'error',
      })
      return
    }

    try {
      setUploading(true)
      const response = await bulkUploadService(item, csvData)

      toast(<ToastMessage message={response?.message || uploadSuccess} />, {
        ...toastOptions,
        type: 'success',
      })
      setValidated(false)
      setCsvData([])
      setSelectedFile(null)
      onClose()
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err?.response?.data?.error || erroruploadfailed || err}
        />,
        {
          ...toastOptions,
          type: 'error',
        }
      )
    } finally {
      setUploading(false)
      setValidated(false)
      setCsvData([])
      setSelectedFile(null)
    }
  }

  const readFile = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer)
          const workbook = XLSX.read(data, { type: 'array' })
          const worksheet = workbook.Sheets[workbook.SheetNames[0]]
          const json = XLSX.utils.sheet_to_json(worksheet, { defval: '' })
          resolve(json)
        } catch (err) {
          reject(err)
        }
      }
      reader.onerror = (err) => reject(err)
      reader.readAsArrayBuffer(file)
    })
  }
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Import</DialogTitle>
      <DialogContent>
        <p>
          {UploadCSV} {item ? item : ''} data.
        </p>
        <input
          type='file'
          accept='.csv, .xlsx'
          data-testid='file-input'
          onChange={handleFileSelect}
          style={{ marginTop: '10px' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!validated ? (
          <Button
            onClick={handleValidate}
            disabled={!selectedFile || uploading}
            variant='contained'
          >
            Validate
          </Button>
        ) : (
          <Button
            data-testid='upload-btn'
            onClick={handleProcess}
            disabled={uploading}
            variant='contained'
            color='success'
          >
            {uploading ? BUTTON_TEXT.PROCESSING : BUTTON_TEXT.PROCESS}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default AdminUploadDialog
